import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Upload, Download, Trash2, FileText, Plus, Vote } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function CommunityMinutesPage() {
  const [minutes, setMinutes] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", date: "", attendees: "", file: null });
  const [agForm, setAgForm] = useState({ title: "", description: "", date: "", minutes_id: "", votes: [] });
  const [tab, setTab] = useState("minutes");

  const load = useCallback(async () => {
    try {
      const [mRes, aRes, oRes] = await Promise.all([
        axios.get(`${API}/community/minutes`),
        axios.get(`${API}/community/agreements`),
        axios.get(`${API}/community/owners`),
      ]);
      setMinutes(mRes.data); setAgreements(aRes.data); setOwners(oRes.data);
    } catch (e) { if (e.response?.status !== 401) toast.error("Error cargando"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) { toast.error("Titulo y archivo requeridos"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("title", uploadForm.title);
      fd.append("date", uploadForm.date);
      fd.append("attendees", uploadForm.attendees);
      await axios.post(`${API}/community/minutes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Acta subida"); setUploadOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/community/minutes/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", doc.original_filename);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando"); }
  };

  const handleDeleteMinutes = async (id) => {
    if (!window.confirm("¿Eliminar acta?")) return;
    try { await axios.delete(`${API}/community/minutes/${id}`); toast.success("Eliminada"); load(); }
    catch { toast.error("Error"); }
  };

  const openNewAgreement = () => {
    setAgForm({ title: "", description: "", date: new Date().toISOString().split('T')[0], minutes_id: "", votes: owners.map(o => ({ owner_id: o.id, owner_name: o.name, vote: "" })) });
    setAgreementOpen(true);
  };

  const handleSaveAgreement = async () => {
    try { await axios.post(`${API}/community/agreements`, agForm); toast.success("Acuerdo registrado"); setAgreementOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDeleteAgreement = async (id) => {
    if (!window.confirm("¿Eliminar acuerdo?")) return;
    try { await axios.delete(`${API}/community/agreements/${id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const updateVote = (idx, vote) => {
    const v = [...agForm.votes]; v[idx].vote = vote; setAgForm({...agForm, votes: v});
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="community-minutes-page">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Actas y Acuerdos</h2>
        <div className="flex gap-2">
          <button onClick={() => { setUploadForm({ title: "", date: new Date().toISOString().split('T')[0], attendees: "", file: null }); setUploadOpen(true); }} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2" data-testid="upload-minutes-button"><Upload className="w-4 h-4" /> Subir acta</button>
          <button onClick={openNewAgreement} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2" data-testid="add-agreement-button"><Vote className="w-4 h-4" /> Nuevo acuerdo</button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("minutes")} className={`px-4 py-1.5 text-sm font-semibold rounded-sm transition-all ${tab === "minutes" ? "bg-[#002FA7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>Actas</button>
        <button onClick={() => setTab("agreements")} className={`px-4 py-1.5 text-sm font-semibold rounded-sm transition-all ${tab === "agreements" ? "bg-[#002FA7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>Acuerdos ({agreements.length})</button>
      </div>

      {tab === "minutes" && (
        <div className="bg-white border border-gray-200 rounded-sm">
          <Table><TableHeader><TableRow className="bg-gray-50">
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Fecha</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Titulo</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Archivo</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Asistentes</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {minutes.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400">No hay actas</TableCell></TableRow> :
            minutes.map(m => (
              <TableRow key={m.id} className="hover:bg-gray-50">
                <TableCell className="text-sm text-gray-600 py-3 px-4">{m.date}</TableCell>
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4">{m.title}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4 flex items-center gap-1"><FileText className="w-3 h-3" />{m.original_filename}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4 max-w-[200px] truncate">{m.attendees || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => handleDownload(m)} className="p-1.5 text-gray-400 hover:text-[#002FA7]"><Download className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteMinutes(m.id)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody></Table>
        </div>
      )}

      {tab === "agreements" && (
        <div className="space-y-4">
          {agreements.length === 0 ? <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-400">No hay acuerdos registrados</div> :
          agreements.map(a => (
            <div key={a.id} className="bg-white border border-gray-200 rounded-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-bold text-gray-900">{a.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{a.date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-bold ${a.result === "aprobado" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : a.result === "rechazado" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{a.result?.toUpperCase()}</span>
                  <button onClick={() => handleDeleteAgreement(a.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">{a.description}</p>
              {a.votes && a.votes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {a.votes.map((v, i) => (
                    <span key={i} className={`text-xs px-2 py-0.5 rounded-sm font-medium ${v.vote === "a_favor" ? "bg-emerald-50 text-emerald-700" : v.vote === "en_contra" ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                      {v.owner_name}: {v.vote === "a_favor" ? "A favor" : v.vote === "en_contra" ? "En contra" : v.vote === "abstencion" ? "Abstencion" : "—"}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Acta Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded-sm"><DialogHeader><DialogTitle>Subir acta de junta</DialogTitle><DialogDescription>Sube el acta en PDF o Word</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Titulo</Label><Input value={uploadForm.title} onChange={e => setUploadForm({...uploadForm, title: e.target.value})} placeholder="Junta ordinaria..." className="rounded-sm" data-testid="minutes-title-input" /></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Fecha</Label><Input type="date" value={uploadForm.date} onChange={e => setUploadForm({...uploadForm, date: e.target.value})} className="rounded-sm" /></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Asistentes</Label><Input value={uploadForm.attendees} onChange={e => setUploadForm({...uploadForm, attendees: e.target.value})} placeholder="Nombres separados por comas" className="rounded-sm" /></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Archivo</Label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={e => setUploadForm({...uploadForm, file: e.target.files[0]})} className="text-sm text-gray-600" data-testid="minutes-file-input" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setUploadOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all">Cancelar</button>
            <button onClick={handleUpload} disabled={uploading} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all disabled:opacity-50" data-testid="upload-minutes-submit">{uploading ? "Subiendo..." : "Subir"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Agreement Dialog */}
      <Dialog open={agreementOpen} onOpenChange={setAgreementOpen}>
        <DialogContent className="rounded-sm max-w-lg"><DialogHeader><DialogTitle>Nuevo acuerdo</DialogTitle><DialogDescription>Registra un acuerdo con votacion</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Titulo</Label><Input value={agForm.title} onChange={e => setAgForm({...agForm, title: e.target.value})} className="rounded-sm" data-testid="agreement-title-input" /></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Descripcion</Label><Input value={agForm.description} onChange={e => setAgForm({...agForm, description: e.target.value})} className="rounded-sm" /></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Fecha</Label><Input type="date" value={agForm.date} onChange={e => setAgForm({...agForm, date: e.target.value})} className="rounded-sm" /></div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-2 block">Votaciones</Label>
              <div className="space-y-2">
                {agForm.votes.map((v, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-sm">
                    <span className="text-sm text-gray-900 flex-1">{v.owner_name}</span>
                    <Select value={v.vote} onValueChange={val => updateVote(i, val)}>
                      <SelectTrigger className="w-32 rounded-sm h-8 text-xs"><SelectValue placeholder="Voto" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="a_favor">A favor</SelectItem>
                        <SelectItem value="en_contra">En contra</SelectItem>
                        <SelectItem value="abstencion">Abstencion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {agForm.votes.length === 0 && <p className="text-xs text-gray-400">Crea propietarios primero para registrar votos</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAgreementOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all">Cancelar</button>
            <button onClick={handleSaveAgreement} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="agreement-save-button">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
